"use client";

import * as React from "react";

/**
 * Wrapper around the browser's SpeechSynthesis API for English pronunciation.
 * Returns helpers + speaking state.
 */
export function useTextToSpeech() {
  const [speaking, setSpeaking] = React.useState(false);
  const [supported, setSupported] = React.useState(true);

  React.useEffect(() => {
    if (typeof window === "undefined" || !("speechSynthesis" in window)) {
      setSupported(false);
    }
  }, []);

  const speak = React.useCallback(
    (text: string, opts?: { rate?: number; voice?: string }) => {
      if (!supported) return;
      const synth = window.speechSynthesis;
      synth.cancel();
      const utter = new SpeechSynthesisUtterance(text);
      utter.lang = "en-US";
      utter.rate = opts?.rate ?? 0.95;
      // Prefer an English voice if available
      const voices = synth.getVoices();
      const enVoice =
        voices.find((v) => v.lang.startsWith("en") && /Google|Samantha|Daniel|Karen/.test(v.name)) ||
        voices.find((v) => v.lang.startsWith("en"));
      if (enVoice) utter.voice = enVoice;
      utter.onstart = () => setSpeaking(true);
      utter.onend = () => setSpeaking(false);
      utter.onerror = () => setSpeaking(false);
      synth.speak(utter);
    },
    [supported]
  );

  const stop = React.useCallback(() => {
    if (!supported) return;
    window.speechSynthesis.cancel();
    setSpeaking(false);
  }, [supported]);

  return { speak, stop, speaking, supported };
}
